import { Button, Grid, Input, Modal } from '@design-systems/canvas-ui'
import Icon, { ErrorFillIcon } from '@design-systems/react-icons'
import { CustomTooltip } from 'mvscommoncomponents'
import { useCallback, useEffect, useState } from 'react'
import { BackgroundOverlay, CheckboxTable } from '../../../common'
import {
  ENABLE_NEXT_BUTTON_TOOLTIP,
  MSG_APPROVE_CAPABILITIES,
  MSG_CAPABALITIES_APPROVED,
  POPUP_HEADER_CAPABILITIES_EDITABLE,
  POPUP_HEADER_CAPABILITIES_READONLY,
} from '../../../common/commonConstants'

/**
 * Popup for RFI1 T2 for capability specific approvals
 */
const RfiProductCapabilitiesConfimationModal = (props) => {
  const [comments, setComments] = useState('')
  const [capabilitiesList, setCapabilitiesList] = useState([])
  const [isEditMode, setIsEditMode] = useState(!props.settings?.isReadOnly)
  const [summaryCounts, setSummaryCounts] = useState({
    totalSelectedCount: 0,
    targetDesignatedCount: 0,
    vendorSourcedCount: 0,
    gmiCertifiedCount: 0,
  })

  const isCapabilityManagementMode = !!props.isCapabilityManagementMode
  const isReadOnly = isCapabilityManagementMode
    ? !isEditMode
    : !!props.settings?.isReadOnly

  const renameSecondaryToSubType = (list = []) =>
    list.map((c) => {
      if (c && 'secondary_type' in c && !('sub_type' in c)) {
        const { secondary_type, ...rest } = c
        return {
          ...rest,
          sub_type: secondary_type,
          vetting_status: c.vetting_status,
        }
      }
      return c
    })

  useEffect(() => {
    setCapabilitiesList(renameSecondaryToSubType(props.capabilitiesList || []))
  }, [props.capabilitiesList])

  useEffect(() => {
    if (props.settings?.isVisible) {
      setIsEditMode(!props.settings?.isReadOnly)
    }
  }, [props.settings?.isVisible, props.settings?.isReadOnly])

  /**
   * Reset the capabilities popup to original values and close the popup
   */
  const resetDismissPopup = () => {
    setCapabilitiesList(renameSecondaryToSubType(props.capabilitiesList || []))
    setComments('')
    setIsEditMode(!props.settings?.isReadOnly)
    props.closeRFIProductCapabilitiesConfirmationModal()
  }

  /**
   * Condition to enable next button
   */
  const isNextButtonDisabled = () => {
    return (
      !comments?.trim().length ||
      capabilitiesList?.every(
        (capability) => !capability.vetting_status?.length,
      )
    )
  }

  const toApiShape = (list = []) =>
    list.map(({ sub_type, ...rest }) =>
      sub_type != null ? { ...rest, secondary_type: sub_type } : rest,
    )

  const handleCountsChange = useCallback((counts) => {
    setSummaryCounts((previousCounts) => {
      const sameTotal =
        previousCounts.totalSelectedCount === counts?.totalSelectedCount
      const sameTarget =
        previousCounts.targetDesignatedCount === counts?.targetDesignatedCount
      const sameVendor =
        previousCounts.vendorSourcedCount === counts?.vendorSourcedCount
      const sameGmi =
        previousCounts.gmiCertifiedCount === counts?.gmiCertifiedCount

      if (sameTotal && sameTarget && sameVendor && sameGmi) {
        return previousCounts
      }

      return {
        totalSelectedCount: counts?.totalSelectedCount || 0,
        targetDesignatedCount: counts?.targetDesignatedCount || 0,
        vendorSourcedCount: counts?.vendorSourcedCount || 0,
        gmiCertifiedCount: counts?.gmiCertifiedCount || 0,
      }
    })
  }, [])

  const headerOptions = props.specifications?.options || []
  const targetValue =
    headerOptions.find((option) =>
      String(option?.label || '')
        .toLowerCase()
        .includes('target'),
    )?.value ?? 0
  const vendorValue =
    headerOptions.find((option) =>
      String(option?.label || '')
        .toLowerCase()
        .includes('vendor'),
    )?.value ?? 1

  const gmiValue =
    headerOptions.find((option) =>
      String(option?.label || '')
        .toLowerCase()
        .includes('gmi'),
    )?.value ?? 2

  const gmiOption = headerOptions.find(
    (option) => option?.value === gmiValue,
  ) ||
    headerOptions[2] || { label: 'GMI Certified', value: 2 }

  const targetOption = headerOptions.find(
    (option) => option?.value === targetValue,
  ) ||
    headerOptions[0] || { label: 'Target Designated', value: 0 }
  const vendorOption = headerOptions.find(
    (option) => option?.value === vendorValue,
  ) ||
    headerOptions[1] || { label: 'Vendor Sourced', value: 1 }

  return (
    <Modal
      id="modal-product-capabilities-action"
      headingText={
        <Grid.Item className="pc-modal-heading">
          <div>
            {isCapabilityManagementMode
              ? isReadOnly
                ? 'View Product Capabilities'
                : 'Manage Product Capabilities'
              : props.settings.isReadOnly
                ? POPUP_HEADER_CAPABILITIES_READONLY
                : POPUP_HEADER_CAPABILITIES_EDITABLE}
          </div>
          <div className="hc8-pl-3x">
            <span className="pc-mode-badge hc8-pv-1x hc8-ph-2x hc8-fs-050 hc8-clr-brand">
              {isReadOnly ? 'View Only' : 'Edit Mode'}
            </span>
          </div>
        </Grid.Item>
      }
      size={
        props.specifications?.windowSize === 'dense'
          ? 'md'
          : props.specifications?.windowSize || 'md'
      }
      isOpen={props.settings.isVisible}
      onClose={resetDismissPopup}
      className="lcm-product-capabilities-modal"
    >
      <Grid.Container className="pc-modal-content hc8-ph-6x hc8-pb-6x hc8-pt-3x">
        {isCapabilityManagementMode && (
          <>
            <Grid.Item
              size={{ xs: 12 }}
              className="hc8-clr-placeholder hc8-fs-100 hc8-pl-none hc8-pt-none hc8-pb-3x"
            >
              {isReadOnly
                ? 'Review approved capabilities across Target Designated and Vendor Sourced dimensions'
                : 'Modify capability approvals across Target Designated and Vendor Sourced dimensions'}
            </Grid.Item>

            <Grid.Item
              size={{ xs: 12 }}
              className="hc8-pl-none hc8-pr-none hc8-pb-3x"
            >
              <div className="pc-summary hc8-bg-subdued hc8-ph-3x hc8-pv-3x hc8-fs-100 hc8-clr-placeholder">
                <strong>
                  Total Selected: {summaryCounts.totalSelectedCount}
                </strong>
                <span className="hc8-ml-3x hc8-mr-3x">|</span>
                {targetOption.label}:{' '}
                <span className="hc8-clr-brand">
                  {summaryCounts.targetDesignatedCount}
                </span>
                <span className="hc8-ml-3x hc8-mr-3x">|</span>
                {vendorOption.label}:{' '}
                <span className="hc8-clr-brand">
                  {summaryCounts.vendorSourcedCount}
                </span>
                {props.rfiType === 'questionnaire' &&
                  props.manufacturingTypeId === '27' && (
                    <>
                      <span className="hc8-ml-3x hc8-mr-3x">|</span>
                      {gmiOption.label}:{' '}
                      <span className="hc8-clr-brand">
                        {summaryCounts.gmiCertifiedCount}
                      </span>
                    </>
                  )}
              </div>
            </Grid.Item>
          </>
        )}

        <Grid.Item
          size={{ xs: 12 }}
          className="hc8-clr-placeholder hc8-fs-100 hc8-pl-none hc8-pt-none"
        >
          {isCapabilityManagementMode
            ? ''
            : props.settings.isReadOnly
              ? MSG_CAPABALITIES_APPROVED
              : MSG_APPROVE_CAPABILITIES}
        </Grid.Item>

        {/* Table to represent product capabilities to be selected */}
        <Grid.Item
          size={{ xs: 12 }}
          className="pc-table-wrap hc8-clr-placeholder hc8-pl-none hc8-pr-none hc8-pt-3x hc8-wth-100"
        >
          <div className="pc-table-surface">
            <CheckboxTable
              isReadOnly={isReadOnly}
              capabilitiesList={capabilitiesList}
              updateCapabilitiesList={(updatedCapabilities) =>
                setCapabilitiesList(
                  renameSecondaryToSubType(updatedCapabilities),
                )
              }
              onCountsChange={handleCountsChange}
              manufacturingTypeId={props.manufacturingTypeId}
              rfiType={props.rfiType}
              specifications={props.specifications || {}}
            />
          </div>
        </Grid.Item>

        {!isCapabilityManagementMode && (
          <>
            <Grid.Item size={{ xs: 12 }} className="hc8-pl-none hc8-pr-none">
              <Input.Label className="hc8-fs-050">
                Comments (Why were other skills not approved?)
                <span className="hc8-clr-error">*</span>
              </Input.Label>
              <Input.Textarea
                data-testid="textarea-capability-comment"
                id="textarea-capability-comment"
                isDisabled={props.settings.isReadOnly}
                value={
                  props.settings.isReadOnly ? props.existingComment : comments
                }
                placeholder={props.settings.isReadOnly ? '' : 'Comments'}
                onChange={(event) => setComments(event.target.value)}
                isErrored={
                  !comments?.length || comments?.length >= props.commentsMaxLen
                }
              />
              {!props.settings.isReadOnly && (
                <Input.Label className="pc-comment-counter hc8-clr-placeholder hc8-fs-150 hc8-mt-2x">{`${comments?.length} / ${props.commentsMaxLen}`}</Input.Label>
              )}
            </Grid.Item>

            {!props.settings.isReadOnly && (
              <Grid.Item
                size={{ xs: 12 }}
                className="pc-actions hc8-pb-2x hc8-pt-4x"
              >
                <Button
                  variant="secondary"
                  size="md"
                  className="hc8-mr-3x"
                  onClick={resetDismissPopup}
                  data-testid="close-button"
                >
                  Close
                </Button>
                <CustomTooltip
                  title={
                    isNextButtonDisabled() ? ENABLE_NEXT_BUTTON_TOOLTIP : ''
                  }
                  placement="top"
                  arrow
                  component={
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => {
                        const payload = toApiShape(capabilitiesList)
                        props.onNextActionConfirmation(comments, payload)
                      }}
                      isDisabled={isNextButtonDisabled()}
                      data-testid="next-button"
                    >
                      Next
                      {!props.isRfiNextActionPCInProgress &&
                        props.isRfiNextActionPCFailed && (
                          <div className="hc8-pl-1x">
                            <Icon
                              svg={ErrorFillIcon}
                              className="hc8-clr-error"
                              size="md"
                              aria-hidden
                            />
                          </div>
                        )}
                    </Button>
                  }
                />
              </Grid.Item>
            )}
          </>
        )}

        {isCapabilityManagementMode && (
          <>
            <Grid.Item
              size={{ xs: 12 }}
              className="hc8-pl-none hc8-pr-none hc8-pt-3x"
            >
              <div className="pc-note hc8-bg-subdued hc8-pa-3x hc8-fs-100 hc8-clr-placeholder">
                <strong>{isReadOnly ? 'View Mode:' : 'Tip:'}</strong>{' '}
                {isReadOnly
                  ? 'This is a read-only view of approved capabilities. Click "Edit Capabilities" to make changes to the approvals.'
                  : 'You can approve a capability for both dimensions simultaneously if needed. Modified items are highlighted. Review your changes before saving.'}
              </div>
            </Grid.Item>

            <Grid.Item
              size={{ xs: 12 }}
              className="hc8-pl-none hc8-pr-none hc8-pt-3x"
            >
              <Grid.Container
                align="center"
                justify="space-between"
                className="pc-management-actions"
              >
                <Grid.Item
                  size={{ xs: 5 }}
                  className="hc8-fs-100 hc8-clr-disabled"
                >
                  {isReadOnly
                    ? ``
                    : `${summaryCounts.totalSelectedCount} ${summaryCounts.totalSelectedCount > 1 ? 'capabilities will be updated' : 'capability will be updated'}`}
                </Grid.Item>
                <Grid.Item size={{ xs: 7 }} className="pc-management-buttons">
                  <Button
                    variant="secondary"
                    size="md"
                    className="hc8-mr-3x"
                    onClick={resetDismissPopup}
                    data-testid="close-button"
                  >
                    {isReadOnly ? 'Close' : 'Cancel'}
                  </Button>
                  {isReadOnly ? (
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => setIsEditMode(true)}
                      data-testid="edit-capabilities-button"
                    >
                      Edit Capabilities
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="md"
                      onClick={() => {
                        const payload = toApiShape(capabilitiesList)
                        props.onNextActionConfirmation(
                          props.existingComment || comments || '',
                          payload,
                          isCapabilityManagementMode,
                        )
                      }}
                      isDisabled={props.isRfiNextActionPCInProgress}
                      data-testid="save-capabilities-button"
                    >
                      Save Changes
                    </Button>
                  )}
                </Grid.Item>
              </Grid.Container>
            </Grid.Item>
          </>
        )}
      </Grid.Container>

      {props.isRfiNextActionPCInProgress && !props.isRfiNextActionPCFailed && (
        <BackgroundOverlay showSpinner />
      )}
    </Modal>
  )
}

export default RfiProductCapabilitiesConfimationModal
